import { z } from 'zod'
import { idSchema, isoDateTimeSchema, minorNonNegativeSchema, optionalText, text } from './common.js'

export const LINE_KINDS = ['part', 'service', 'material'] as const
export type LineKind = (typeof LINE_KINDS)[number]
export const lineKindSchema = z.enum(LINE_KINDS)

export const LINE_KIND_LABELS: Record<LineKind, string> = {
  part: 'Parts used',
  service: 'Services done',
  material: 'Materials used',
}
export const LINE_KIND_SINGULAR: Record<LineKind, string> = {
  part: 'Part',
  service: 'Service',
  material: 'Material',
}

/**
 * Every kind can carry a quantity, services included — e.g. 2 hours of
 * labour, or a job performed twice. The line total is already
 * quantity × rate for every kind; this only ever hid the column.
 */
export function kindHasQuantity(_kind: LineKind): boolean {
  return true
}
/**
 * Every kind can carry our cost, services included.
 *
 * Services were originally rate-only. Without a labour cost the finance report
 * has to treat all service revenue as pure profit, which overstates the margin
 * on the highest-margin-looking part of the business. The field stays OPTIONAL:
 * lines written before it existed keep a null cost and are reported as
 * "cost not recorded" rather than as zero.
 */
export function kindHasCost(_kind: LineKind): boolean {
  return true
}
/** Only parts have a manufacturer part number. */
export function kindHasPartNumber(kind: LineKind): boolean {
  return kind === 'part'
}

export const catalogItemInputSchema = z.object({
  kind: lineKindSchema,
  name: text(200),
  partNumber: optionalText(80),
  defaultCostMinor: minorNonNegativeSchema.nullable().default(null),
  defaultRateMinor: minorNonNegativeSchema,
})
export type CatalogItemInput = z.infer<typeof catalogItemInputSchema>

export const catalogItemSchema = catalogItemInputSchema.extend({
  id: idSchema,
  timesUsed: z.number().int().min(0),
  lastUsedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type CatalogItem = z.infer<typeof catalogItemSchema>

/**
 * The uniqueness key for a catalogue entry. Case- and whitespace-insensitive on
 * the name, so "Oil filter", "oil filter" and " Oil  Filter " are one entry
 * rather than three near-duplicates cluttering every autocomplete.
 */
export function catalogKey(kind: LineKind, name: string, partNumber: string | null): string {
  const normalisedName = name.trim().toLowerCase().replace(/\s+/g, ' ')
  const normalisedPart = (partNumber ?? '').trim().toLowerCase()
  return `${kind} ${normalisedName} ${normalisedPart}`
}
